import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Layout, Header } from '../components/mte/shell';
import { apiFetch, ApiError } from '../lib/auth-fetch';
import styles from './ChoixHeros.module.css';

type HeroId = 'ninja' | 'lobo' | 'princesa' | 'fada' | 'gato' | 'dragao';
type HeroVariant = 'noir' | 'blanc';
type FetchState = 'idle' | 'loading' | 'ready' | 'error';

interface Child {
  childId: string;
  firstName: string;
  schoolLevel: 'CP' | 'CE1' | 'CE2' | 'CM1' | 'CM2';
  heroId: HeroId | null;
  heroVariant?: HeroVariant | null;
}

const CAT_BLACK_IDLE = '/heroes/gato/preto/ChatGPT Image 16_09_2026, 10_32_25 (1).png';
const CAT_WHITE_IDLE = '/heroes/gato/branco/ChatGPT Image 17_09_2026, 14_32_21 (1).png';
const DIAGNOSTIC_SESSION_KEY = 'mte:diagnostic:session:v1';

const HEROES: Array<{ id: HeroId; label: string; src: string }> = [
  { id: 'ninja', label: 'Ninja', src: '/choix-heros/ninja-final.webp' },
  { id: 'lobo', label: 'Loup', src: '/choix-heros/loup-final.webp' },
  { id: 'princesa', label: 'Princesse', src: '/choix-heros/princesse-final.webp' },
  { id: 'fada', label: 'Fée', src: '/choix-heros/fee-final.webp' },
  { id: 'gato', label: 'Chat', src: '/choix-heros/chat-final.webp' },
  { id: 'dragao', label: 'Dragon', src: '/choix-heros/dragon-final.webp' },
];

const HERO_LABELS: Record<HeroId, string> = Object.fromEntries(
  HEROES.map((hero) => [hero.id, hero.label]),
) as Record<HeroId, string>;

function ChoixHeros(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const childId = searchParams.get('childId');
  const mode = searchParams.get('mode');

  const [child, setChild] = useState<Child | null>(null);
  const [fetchState, setFetchState] = useState<FetchState>('idle');
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [heroSelectionne, setHeroSelectionne] = useState<HeroId | null>(null);
  const [heroVariant, setHeroVariant] = useState<HeroVariant | null>(null);
  const [patching, setPatching] = useState(false);
  const [patchError, setPatchError] = useState<string | null>(null);

  useEffect(() => {
    if (!childId) navigate('/famille/accueil/', { replace: true });
  }, [childId, navigate]);

  useEffect(() => {
    if (!childId) return;
    let cancelled = false;
    (async () => {
      setFetchState('loading');
      try {
        const data = await apiFetch<Child>(`/api/parent/children/${encodeURIComponent(childId)}`);
        if (cancelled) return;
        setChild(data);
        if (mode === 'rechoice' && data.heroId) {
          setHeroSelectionne(data.heroId);
          setHeroVariant(data.heroId === 'gato' ? (data.heroVariant ?? null) : null);
        }
        setFetchState('ready');
        setFetchError(null);
      } catch (e) {
        if (cancelled) return;
        const isNotFound = e instanceof ApiError && e.status === 404;
        if (isNotFound) {
          navigate('/famille/accueil/', { replace: true });
          return;
        }
        setFetchState('error');
        setFetchError(e instanceof ApiError ? e.message : "Impossible de charger les informations de l'enfant. Réessaie.");
      }
    })();
    return () => { cancelled = true; };
  }, [childId, mode, navigate]);

  const handleSelect = (id: HeroId): void => {
    setHeroSelectionne(id);
    if (id !== 'gato') setHeroVariant(null);
    setPatchError(null);
  };

  const handleConfirm = async (): Promise<void> => {
    if (!child || !heroSelectionne) return;
    if (heroSelectionne === 'gato' && !heroVariant) return;

    setPatching(true);
    setPatchError(null);
    try {
      const payload = heroSelectionne === 'gato'
        ? { heroId: heroSelectionne, heroVariant }
        : { heroId: heroSelectionne };

      await apiFetch(`/api/parent/children/${encodeURIComponent(child.childId)}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });

      if (mode !== 'rechoice' && typeof window !== 'undefined') {
        try {
          window.localStorage.removeItem(DIAGNOSTIC_SESSION_KEY);
        } catch {
          // Non bloquant : si le stockage est indisponible, on continue quand même.
        }
      }

      if (mode === 'rechoice') {
        navigate(`/enfant/aventure/?childId=${encodeURIComponent(child.childId)}`);
      } else {
        navigate(`/enfant/diagnostic/intro/?childId=${encodeURIComponent(child.childId)}`);
      }
    } catch (e) {
      setPatchError(e instanceof ApiError ? e.message : "Impossible d'enregistrer le héros. Réessaie.");
    } finally {
      setPatching(false);
    }
  };

  const handleSkipLater = (): void => navigate('/famille/accueil/');

  return (
    <Layout
      context="enfant"
      width="default"
      header={
        <Header
          context="enfant"
          logo={
            <>
              <span className={styles.brandMark} aria-hidden="true">✦</span>
              <span className={styles.brandText}>Mon Temps d&apos;Étude</span>
            </>
          }
        />
      }
      footer={
        <footer className={styles.footer}>
          <p><Link to="/famille/accueil/">← Retour à l&apos;accueil</Link></p>
        </footer>
      }
    >
      <main className={styles.main} aria-labelledby="choix-heros-title">
        <div className={styles.stageShade} aria-hidden="true" />

        <section className={styles.stageContent}>
          <div className={styles.headingBlock}>
            <p className={styles.eyebrow}>ÉTAPE 3 — TON HÉROS</p>
            <h1 id="choix-heros-title" className={styles.title}>Choisis ton héros</h1>
            <p className={styles.lede}>Choisis le héros qui te ressemble. Il te représentera tout au long de ta mission et évoluera avec toi.</p>
          </div>

          {fetchState === 'loading' && (
            <section className={styles.loadingSection} aria-busy="true">
              <p className={styles.loadingMsg}>Chargement...</p>
            </section>
          )}

          {fetchState === 'error' && (
            <section className={styles.errorSection} role="alert">
              <p className={styles.errorMsg}>{fetchError ?? 'Impossible de charger les informations.'}</p>
              <Link to="/famille/accueil/" className={styles.retryLink}>Retour à l&apos;accueil</Link>
            </section>
          )}

          {fetchState === 'ready' && child && (
            <>
              <section className={styles.heroGrid} role="radiogroup" aria-label="Choix du héros">
                {HEROES.map((hero) => {
                  const selected = heroSelectionne === hero.id;
                  return (
                    <button
                      key={hero.id}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      aria-label={`Choisir ${hero.label}`}
                      data-hero-id={hero.id}
                      className={selected ? styles.heroCardSelected : styles.heroCard}
                      onClick={() => handleSelect(hero.id)}
                    >
                      <span className={styles.medallion}>
                        <img src={hero.src} alt="" className={styles.heroImage} />
                        {selected && <span className={styles.heroCheck} aria-hidden="true">✓</span>}
                      </span>
                      <span className={styles.heroLabel}>{hero.label}</span>
                    </button>
                  );
                })}
              </section>

              {heroSelectionne === 'gato' && (
                <section className={styles.variantPanel} aria-labelledby="cat-variant-title">
                  <h2 id="cat-variant-title" className={styles.variantTitle}>Quel Chat te ressemble ?</h2>
                  <p className={styles.variantLede}>Choisis ta variante. Tu pourras la changer plus tard.</p>
                  <div className={styles.variantGrid} role="radiogroup" aria-label="Variante du Chat">
                    <button
                      type="button"
                      className={heroVariant === 'noir' ? styles.variantCardSelected : styles.variantCard}
                      onClick={() => setHeroVariant('noir')}
                      role="radio"
                      aria-checked={heroVariant === 'noir'}
                    >
                      <img src={CAT_BLACK_IDLE} alt="Chat noir" />
                      <span>Chat noir</span>
                    </button>
                    <button
                      type="button"
                      className={heroVariant === 'blanc' ? styles.variantCardSelected : styles.variantCard}
                      onClick={() => setHeroVariant('blanc')}
                      role="radio"
                      aria-checked={heroVariant === 'blanc'}
                    >
                      <img src={CAT_WHITE_IDLE} alt="Chat blanc" />
                      <span>Chat blanc</span>
                    </button>
                  </div>
                </section>
              )}

              <section className={styles.actionSection}>
                <p className={styles.actionLabel}>
                  {heroSelectionne ? (
                    <>
                      Tu as choisi <strong>{HERO_LABELS[heroSelectionne]}</strong>
                      {heroSelectionne === 'gato' && heroVariant
                        ? ` ${heroVariant === 'noir' ? 'noir' : 'blanc'}`
                        : ''}. Prêt à commencer l&apos;aventure ?
                    </>
                  ) : (
                    <>Choisis ton héros pour continuer.</>
                  )}
                </p>
                {patchError && <p className={styles.errorMsg} role="alert">{patchError}</p>}
                <div className={styles.actionRow}>
                  <button
                    type="button"
                    className={styles.primaryAction}
                    onClick={handleConfirm}
                    disabled={!heroSelectionne || patching || (heroSelectionne === 'gato' && !heroVariant)}
                  >
                    {patching
                      ? 'Enregistrement...'
                      : heroSelectionne === 'gato' && !heroVariant
                        ? 'Choisis noir ou blanc'
                        : 'Continuer →'}
                  </button>
                </div>
              </section>

              {child.heroId === null && mode !== 'rechoice' && (
                <div className={styles.skipRow}>
                  <button type="button" className={styles.skipLink} onClick={handleSkipLater}>Plus tard</button>
                </div>
              )}
            </>
          )}
        </section>
      </main>
    </Layout>
  );
}

export default ChoixHeros;
